import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { contractApi, customerApi } from '../api';
import { CONTRACT_TYPES, ITEM_CATEGORIES, CONTRACT_STATUSES, CONTRACT_STATUS_COLORS } from '../constants';

interface ContractItem {
  photoContractItemIdx?: number;
  itemCategory: string;
  productName: string;
  productIdx?: number;
  specification: string;
  quantity: number;
  unitPrice: number;
  costPrice: number;
  memo: string;
  workStatus?: string;
}

const emptyItem = (): ContractItem => ({
  itemCategory: 'ALBUM', productName: '', specification: '',
  quantity: 1, unitPrice: 0, costPrice: 0, memo: '',
});

export default function PhotoContractDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('id') ? Number(searchParams.get('id')) : null;
  const preselectedCustomerId = searchParams.get('customerId') ? Number(searchParams.get('customerId')) : null;

  const [isLoading, setIsLoading] = useState(false);
  const [contract, setContract] = useState<any>(null);

  const [form, setForm] = useState({
    photoCustomerIdx: preselectedCustomerId || '',
    contractType: 'REHEARSAL_AND_MAIN',
    contractDt: new Date().toISOString().split('T')[0],
    totalAmount: 0,
    discountAmount: 0,
    shootingDt: '',
    commissionRate: '',
    partnerVendorName: '',
    memo: '',
    status: 'ACTIVE',
  });

  const [items, setItems] = useState<ContractItem[]>([emptyItem()]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<any[]>([]);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');

  const fetchContract = useCallback(async () => {
    if (!contractId) return;
    setIsLoading(true);
    try {
      const data = await contractApi.detail(contractId);
      setContract(data);
      setForm({
        photoCustomerIdx: data.photoCustomerIdx || data.photoCustomer?.photoCustomerIdx || '',
        contractType: data.contractType || 'REHEARSAL_AND_MAIN',
        contractDt: data.contractDt || '',
        totalAmount: data.totalAmount || 0,
        discountAmount: data.discountAmount || 0,
        shootingDt: data.shootingDt || '',
        commissionRate: data.commissionRate || '',
        partnerVendorName: data.partnerVendorName || '',
        memo: data.memo || '',
        status: data.status || 'ACTIVE',
      });
      const cust = data.photoCustomer;
      if (cust) {
        setSelectedCustomerName(`${cust.groomName || ''} / ${cust.brideName || ''}`.replace(/^ \/ | \/ $/g, ''));
      } else if (data.photoCustomerIdx) {
        try {
          const custData = await customerApi.detail(data.photoCustomerIdx, 'OVERVIEW');
          const c = custData.customer;
          setSelectedCustomerName(c ? `${c.groomName || ''} / ${c.brideName || ''}`.replace(/^ \/ | \/ $/g, '') : '');
        } catch {}
      }
      setItems(data.items?.length ? data.items.map((item: any) => ({ ...item })) : [emptyItem()]);
    } catch (err) {
      console.error('계약 상세 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [contractId]);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  const searchCustomers = useCallback(async (q: string) => {
    if (!q) { setCustomerResults([]); return; }
    try {
      const data = await customerApi.list({ q, size: 10 });
      setCustomerResults(data.items || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchCustomers(customerSearch), 300);
    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);

  const finalAmount = (form.totalAmount || 0) - (form.discountAmount || 0);

  const handleItemChange = (index: number, field: keyof ContractItem, value: any) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (index: number) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.photoCustomerIdx) { alert('고객을 선택해주세요.'); return; }
    if (items.some(item => !item.productName || !item.unitPrice)) { alert('모든 품목의 상품명과 단가를 입력해주세요.'); return; }

    const { shootingDt, commissionRate, ...cleanForm } = form;
    const payload = {
      ...cleanForm,
      photoCustomerIdx: Number(form.photoCustomerIdx),
      totalAmount: Number(form.totalAmount),
      discountAmount: Number(form.discountAmount),
      items: items.map(item => ({
        ...item,
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
        costPrice: item.costPrice ? Number(item.costPrice) : undefined,
      })),
    };

    try {
      if (contractId) {
        const { items: _, photoCustomerIdx: __, ...headerData } = payload;
        await contractApi.update(contractId, headerData);
        alert('계약이 수정되었습니다.');
      } else {
        await contractApi.create(payload);
        alert('계약이 등록되었습니다.');
      }
      navigate(-1);
    } catch (err: any) {
      alert(`저장 실패: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!contractId) return;
    if (!confirm('정말 이 계약을 삭제하시겠습니까?')) return;
    try {
      await contractApi.delete(contractId);
      alert('삭제되었습니다.');
      navigate('/photo-studio/contracts');
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!contractId) return;
    try {
      await contractApi.update(contractId, { status: newStatus });
      setForm(prev => ({ ...prev, status: newStatus }));
      setContract((prev: any) => prev ? { ...prev, status: newStatus } : prev);
      alert('상태가 변경되었습니다.');
    } catch (err: any) {
      alert(`상태 변경 실패: ${err.message}`);
    }
  };

  const handleAddItemApi = async () => {
    if (!contractId) return;
    const newItem = emptyItem();
    try {
      await contractApi.addItem(contractId, { ...newItem, unitPrice: 0, productName: '(새 품목)' });
      fetchContract();
    } catch (err: any) {
      alert(`품목 추가 실패: ${err.message}`);
    }
  };

  const handleDeleteItemApi = async (itemIdx: number) => {
    if (!confirm('이 품목을 삭제하시겠습니까?')) return;
    try {
      await contractApi.deleteItem(itemIdx);
      fetchContract();
    } catch (err: any) {
      alert(`품목 삭제 실패: ${err.message}`);
    }
  };

  const statusInfo = CONTRACT_STATUSES.find(s => s.value === form.status);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8 max-w-5xl">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-800 w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
              <i className="ri-arrow-left-line text-lg"></i>
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-800">{contractId ? '계약 상세' : '신규 계약'}</h1>
                {contract?.contractNo && (
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{contract.contractNo}</span>
                )}
                {contractId && statusInfo && (
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${CONTRACT_STATUS_COLORS[form.status] || 'bg-gray-100 text-gray-700'}`}>
                    {statusInfo.label}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {contractId && (
              <>
                <select
                  value={form.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {CONTRACT_STATUSES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <button onClick={handleDelete} className="text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors flex items-center gap-1">
                  <i className="ri-delete-bin-line"></i> 삭제
                </button>
              </>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 계약 정보 */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <i className="ri-file-text-line text-gray-400"></i> 계약 정보
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {/* 고객 검색 */}
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-500 mb-1">고객 *</label>
                  {selectedCustomerName ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 bg-blue-50 px-3 py-2 rounded-lg flex-1">{selectedCustomerName}</span>
                      {!contractId && (
                        <button type="button" onClick={() => { setSelectedCustomerName(''); setForm({ ...form, photoCustomerIdx: '' }); }}
                          className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line"></i></button>
                      )}
                    </div>
                  ) : (
                    <>
                      <input type="text" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} placeholder="신랑/신부명 또는 전화번호 검색"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      {customerResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {customerResults.map((c: any) => (
                            <button key={c.photoCustomerIdx} type="button"
                              onClick={() => {
                                setForm({ ...form, photoCustomerIdx: c.photoCustomerIdx });
                                setSelectedCustomerName(`${c.groomName || ''} / ${c.brideName || ''}`);
                                setCustomerSearch('');
                                setCustomerResults([]);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50 flex justify-between cursor-pointer">
                              <span>{c.groomName || ''} / {c.brideName || ''}</span><span className="text-gray-400">{c.groomPhone || c.bridePhone || ''}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">계약유형 *</label>
                  <select value={form.contractType} onChange={(e) => setForm({ ...form, contractType: e.target.value })} required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {CONTRACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">계약일 *</label>
                  <input type="date" value={form.contractDt} onChange={(e) => setForm({ ...form, contractDt: e.target.value })} required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              {/* 금액 정보 */}
              <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">총 금액 *</label>
                  <input type="number" value={form.totalAmount} onChange={(e) => setForm({ ...form, totalAmount: Number(e.target.value) })} required min={0}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">할인 금액</label>
                  <input type="number" value={form.discountAmount} onChange={(e) => setForm({ ...form, discountAmount: Number(e.target.value) })} min={0}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">최종 금액</label>
                  <div className="px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-sm font-bold text-blue-700">
                    {finalAmount.toLocaleString()}원
                  </div>
                </div>
              </div>

              {/* 부가 정보 */}
              <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">발주업체</label>
                  <input type="text" value={form.partnerVendorName} onChange={(e) => setForm({ ...form, partnerVendorName: e.target.value })}
                    placeholder="업체명 입력"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">메모</label>
                <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
            </div>

            {/* 품목 목록 */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <i className="ri-list-check text-gray-400"></i> 품목 목록
                </h3>
                <button type="button" onClick={contractId ? handleAddItemApi : addItem}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 cursor-pointer">
                  <i className="ri-add-line"></i> 품목 추가
                </button>
              </div>
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={item.photoContractItemIdx || index} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500">품목 {index + 1}</span>
                        {item.workStatus && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded font-medium">{item.workStatus}</span>
                        )}
                      </div>
                      {items.length > 1 && (
                        <button type="button" onClick={() => item.photoContractItemIdx ? handleDeleteItemApi(item.photoContractItemIdx) : removeItem(index)}
                          className="text-red-500 hover:text-red-700 text-xs cursor-pointer">삭제</button>
                      )}
                    </div>
                    <div className="grid grid-cols-5 gap-3">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">카테고리 *</label>
                        <select value={item.itemCategory} onChange={(e) => handleItemChange(index, 'itemCategory', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {ITEM_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">상품명 *</label>
                        <input type="text" value={item.productName} onChange={(e) => handleItemChange(index, 'productName', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">규격</label>
                        <input type="text" value={item.specification} onChange={(e) => handleItemChange(index, 'specification', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">수량</label>
                        <input type="number" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value))} min={1}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">단가 *</label>
                        <input type="number" value={item.unitPrice} onChange={(e) => handleItemChange(index, 'unitPrice', Number(e.target.value))} min={0}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">원가</label>
                        <input type="number" value={item.costPrice} onChange={(e) => handleItemChange(index, 'costPrice', Number(e.target.value))} min={0}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div className="col-span-4">
                        <label className="block text-[10px] text-gray-500 mb-0.5">비고</label>
                        <input type="text" value={item.memo} onChange={(e) => handleItemChange(index, 'memo', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 제출 */}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => navigate('/photo-studio/contracts')}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium cursor-pointer text-sm">취소</button>
              <button type="submit"
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer text-sm">
                {contractId ? '수정 완료' : '계약 등록'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
